// DualSense over WebHID: adaptive triggers, rumble and the light bar, which the Gamepad API can't
// reach. Opt-in (Chrome / Edge only; WebHID needs a user gesture to pick the pad), never required:
// every call is a no-op when no pad is open, and any failure closes the link quietly so the
// game falls back to Gamepad API rumble.
//
// Output report layout (Linux hid-playstation, community trigger docs):
//   USB: report 0x02, 47 bytes.  BT: report 0x31, 77 bytes = seq/tag + the same 47 + pad + CRC32.
//   [0] flags0: 0x01 rumble, 0x02 haptics select, 0x04 R2 effect, 0x08 L2 effect
//   [1] flags1: 0x04 light bar, 0x10 player LEDs
//   [2] right (small, high-freq) motor  [3] left (big) motor
//   [10..20] R2 effect (11 bytes)  [21..31] L2 effect
//   [38] flags2 (0x02 light-bar setup, 0x04 rumble v2)  [41] light-bar setup  [42] LED brightness
//   [43] player LEDs  [44..46] RGB
//
// Trigger effects use the zone forms: the travel is 10 zones (0 = rest, 9 = fully in).

type HidDev = {
  opened: boolean; collections: { outputReports?: { reportId: number }[] }[];
  open(): Promise<void>; close(): Promise<void>; sendReport(id: number, data: Uint8Array): Promise<void>;
  addEventListener(t: string, f: () => void): void;
};
type HidApi = {
  requestDevice(o: { filters: { vendorId: number; productId?: number }[] }): Promise<HidDev[]>;
  getDevices(): Promise<HidDev[]>;
  addEventListener(t: string, f: (e: { device: HidDev }) => void): void;
};

const SONY = 0x054c;
const PIDS = [0x0ce6, 0x0df2]; // DualSense, DualSense Edge

/** One trigger effect: 11 bytes. */
export type Trig = Uint8Array;

export const TRIG = {
  off(): Trig { const b = new Uint8Array(11); b[0] = 0x05; return b; },
  /** resistance from `pos` (0..9) to the end, strength 1..8 */
  feedback(pos: number, strength: number): Trig {
    const b = new Uint8Array(11);
    pos = clampI(pos, 0, 9); const f = (clampI(strength, 1, 8) - 1) & 7;
    let zones = 0, force = 0;
    for (let i = pos; i < 10; i++) { zones |= 1 << i; force |= f << (3 * i); }
    b[0] = 0x21; b[1] = zones & 0xff; b[2] = (zones >> 8) & 0xff;
    b[3] = force & 0xff; b[4] = (force >>> 8) & 0xff; b[5] = (force >>> 16) & 0xff; b[6] = (force >>> 24) & 0xff;
    return b;
  },
  /** a gun trigger: resistance from start (2..7) that gives way at end (start+1..8), strength 1..8 */
  weapon(start: number, end: number, strength: number): Trig {
    const b = new Uint8Array(11);
    start = clampI(start, 2, 7); end = clampI(end, start + 1, 8);
    const zones = (1 << start) | (1 << end);
    b[0] = 0x25; b[1] = zones & 0xff; b[2] = (zones >> 8) & 0xff; b[3] = clampI(strength, 1, 8) - 1;
    return b;
  },
  /** buzz from pos (0..9) to the end: amplitude 1..8, frequency Hz 1..255 */
  vibrate(pos: number, amp: number, hz: number): Trig {
    const b = new Uint8Array(11);
    pos = clampI(pos, 0, 9); const a = (clampI(amp, 1, 8) - 1) & 7;
    let zones = 0, force = 0;
    for (let i = pos; i < 10; i++) { zones |= 1 << i; force |= a << (3 * i); }
    b[0] = 0x26; b[1] = zones & 0xff; b[2] = (zones >> 8) & 0xff;
    b[3] = force & 0xff; b[4] = (force >>> 8) & 0xff; b[5] = (force >>> 16) & 0xff; b[6] = (force >>> 24) & 0xff;
    b[9] = clampI(hz, 1, 255);
    return b;
  },
};

function clampI(v: number, a: number, b: number) { return Math.max(a, Math.min(b, Math.round(v))); }

let CRC: Uint32Array | null = null;
function crc32(data: Uint8Array, seed: number): number {
  if (!CRC) {
    CRC = new Uint32Array(256);
    for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; CRC[n] = c >>> 0; }
  }
  let c = 0xffffffff;
  c = CRC[(c ^ seed) & 0xff] ^ (c >>> 8);
  for (let i = 0; i < data.length; i++) c = CRC[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export class DualSense {
  private dev: HidDev | null = null;
  private bt = false;
  private seq = 0;
  private busy = false;
  private dirty = false;
  // wanted state
  private r2: Trig = TRIG.off();
  private l2: Trig = TRIG.off();
  private trigDirty = true;
  private big = 0; private small = 0; private rumbleUntil = 0;
  private rgb: [number, number, number] = [255, 190, 90];
  private leds = 0b00100;
  private ledDirty = true;
  onChange: (on: boolean) => void = () => {};

  static supported() { return typeof navigator !== 'undefined' && !!(navigator as unknown as { hid?: HidApi }).hid; }
  get on() { return !!this.dev; }

  /** Re-open a pad the player already granted (no prompt). */
  async reconnect() {
    const hid = (navigator as unknown as { hid?: HidApi }).hid;
    if (!hid) return false;
    try {
      const list = await hid.getDevices();
      const d = list.find((x) => x.collections.length > 0);
      if (d) return await this.use(d);
    } catch { /* ignore */ }
    return false;
  }

  /** Must run inside a user gesture (a click / button press). */
  async connect() {
    const hid = (navigator as unknown as { hid?: HidApi }).hid;
    if (!hid) return false;
    try {
      const list = await hid.requestDevice({ filters: PIDS.map((p) => ({ vendorId: SONY, productId: p })) });
      if (!list.length) return false;
      return await this.use(list[0]);
    } catch { return false; }
  }

  async disconnect() {
    const d = this.dev;
    if (!d) return;
    this.r2 = TRIG.off(); this.l2 = TRIG.off(); this.big = this.small = 0; this.trigDirty = true;
    await this.flush(true);
    this.dev = null;
    try { await d.close(); } catch { /* ignore */ }
    this.onChange(false);
  }

  private async use(d: HidDev) {
    try {
      if (!d.opened) await d.open();
      // Bluetooth pads expose the 0x31 output report; USB exposes 0x02
      const outs = d.collections.flatMap((c) => c.outputReports ?? []).map((r) => r.reportId);
      this.bt = outs.includes(0x31) && !outs.includes(0x02);
      this.dev = d;
      (navigator as unknown as { hid: HidApi }).hid.addEventListener('disconnect', (e) => { if (e.device === this.dev) { this.dev = null; this.onChange(false); } });
      this.trigDirty = this.ledDirty = true;
      this.onChange(true);
      void this.flush(true);
      return true;
    } catch { this.dev = null; return false; }
  }

  setTriggers(r2: Trig, l2: Trig) {
    if (!same(r2, this.r2)) { this.r2 = r2; this.trigDirty = true; }
    if (!same(l2, this.l2)) { this.l2 = l2; this.trigDirty = true; }
    if (this.trigDirty) this.kick();
  }
  setR2(r2: Trig) { this.setTriggers(r2, this.l2); }
  light(r: number, g: number, b: number, playerSlot: number) {
    const leds = [0b00100, 0b01010, 0b10101, 0b11011][playerSlot & 3];
    if (r === this.rgb[0] && g === this.rgb[1] && b === this.rgb[2] && leds === this.leds) return;
    this.rgb = [r, g, b]; this.leds = leds; this.ledDirty = true; this.kick();
  }
  /** strong = big low motor, weak = small high motor, 0..1 */
  rumble(strong: number, weak: number, ms: number) {
    if (!this.dev) return false;
    this.big = Math.max(this.big, Math.min(1, strong)); this.small = Math.max(this.small, Math.min(1, weak));
    this.rumbleUntil = Math.max(this.rumbleUntil, performance.now() + ms);
    this.kick();
    return true;
  }
  /** call each frame: ends rumble when its time is up */
  update() {
    if (!this.dev) return;
    if ((this.big || this.small) && performance.now() > this.rumbleUntil) { this.big = this.small = 0; this.kick(); }
  }

  private kick() { this.dirty = true; if (!this.busy) void this.flush(false); }

  private async flush(all: boolean) {
    const d = this.dev;
    if (!d) return;
    this.busy = true;
    try {
      while (this.dirty || all) {
        this.dirty = false;
        const c = new Uint8Array(47);
        c[0] = 0x01 | 0x02; // rumble (compatible mode) every report, so it also stops
        c[2] = Math.round(this.small * 255); c[3] = Math.round(this.big * 255);
        c[38] = 0x04; // rumble v2 for current firmware
        if (this.trigDirty || all) { c[0] |= 0x04 | 0x08; c.set(this.r2, 10); c.set(this.l2, 21); this.trigDirty = false; }
        if (this.ledDirty || all) {
          c[1] |= 0x04 | 0x10; if (all) { c[38] |= 0x02; c[41] = 0x02; } // release the pairing glow once
          c[42] = 0x00; c[43] = this.leds;
          c[44] = this.rgb[0]; c[45] = this.rgb[1]; c[46] = this.rgb[2]; this.ledDirty = false;
        }
        all = false;
        if (this.bt) {
          const r = new Uint8Array(77);
          r[0] = (this.seq << 4) & 0xf0; this.seq = (this.seq + 1) & 15;
          r[1] = 0x10;
          r.set(c, 2);
          const crc = crc32(new Uint8Array([0x31, ...r.subarray(0, 73)]), 0xa2);
          r[73] = crc & 0xff; r[74] = (crc >>> 8) & 0xff; r[75] = (crc >>> 16) & 0xff; r[76] = (crc >>> 24) & 0xff;
          await d.sendReport(0x31, r);
        } else await d.sendReport(0x02, c);
      }
    } catch {
      this.dev = null; this.onChange(false);
    } finally { this.busy = false; }
  }
}

function same(a: Uint8Array, b: Uint8Array) {
  for (let i = 0; i < 11; i++) if (a[i] !== b[i]) return false;
  return true;
}
